import Link from 'next/link';
import React, { useCallback, useEffect, useState, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { usePrevious } from '../../../../hooks/usePreviousHook';
import InfoPopout from './components/InfoPopout';
import {
    ContentInformation,
    ContentLink,
    ContentTitle,
    HiddenText,
    ToggleExpansionButton,
    ToggleIcon,
} from './ContentItemInfo.styles';

type LinkTypeResolverProps = {
    linkToDetailsPage: boolean;
    url: string;
    onContentInfoClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    children?: JSX.Element;
};

const LinkTypeResolver = ({
    linkToDetailsPage,
    url,
    onContentInfoClick,
    children,
}: LinkTypeResolverProps) => {
    if (linkToDetailsPage) {
        return (
            <Link href={url} passHref>
                <ContentLink aria-hidden="true" tabIndex={-1} onClick={onContentInfoClick}>
                    {children}
                </ContentLink>
            </Link>
        );
    }

    return children;
};

type ContentItemInfoProps = {
    url: string;
    onContentInfoClick: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => void;
    heading: string;
    subHeading: string;
    longDescription: string;
    editModeActivated: boolean;
    index: number;
    expanded: boolean;
    toggleExpanded: (b: boolean) => void;
    expansionIconVisible: boolean;
    toggleExpansionIconVisible: (b: boolean) => void;
    linkToDetailsPage: boolean;
};

const ContentItemInfo = (props: ContentItemInfoProps): JSX.Element => {
    const {
        url,
        onContentInfoClick,
        heading,
        subHeading,
        longDescription,
        editModeActivated,
        index,
        expanded,
        toggleExpanded,
        expansionIconVisible,
        toggleExpansionIconVisible,
        linkToDetailsPage,
    } = props;
    const [hasExpanded, setHasExpanded] = useState<boolean>(expanded);

    const iconText = expanded ? `Göm info om ${heading}` : `Visa mer info om ${heading}`;
    const a11yFallbackText = expanded ? `Visar mer info om ${heading}` : `Döljer mer info om ${heading}`;
    const a11yText = longDescription && expanded ? longDescription : a11yFallbackText;
    const a11yRef = useRef();
    const showInfo = expanded && !editModeActivated;

    const prevExpanded = usePrevious(expanded);

    useEffect(() => {
        // prevent info expand button from getting ugly focus border
        if (a11yRef.current && prevExpanded !== undefined && expanded !== prevExpanded) {
            // @ts-ignore
            a11yRef.current.focus();
        }

        if (expanded && !hasExpanded) {
            setHasExpanded(true);
        }
    }, [a11yText, expanded, hasExpanded, prevExpanded]);

    // @ts-ignore
    const toggle = useCallback(() => toggleExpanded((state) => !state), [toggleExpanded]);

    const toggleVisibility = (e) => {
        e.preventDefault();
        toggle();
    };

    return (
        <>
            <ContentInformation
                key="meta-transition"
                data-rt="content-item-content"
                hasExpanded={hasExpanded}
                onMouseEnter={() => !editModeActivated && toggleExpansionIconVisible(true)}
                onMouseLeave={() => !editModeActivated && toggleExpansionIconVisible(false)}
            >
                <LinkTypeResolver
                    url={url}
                    onContentInfoClick={onContentInfoClick}
                    linkToDetailsPage={linkToDetailsPage}
                >
                    <>
                        <ContentTitle data-rt="content-item-title" expanded={expanded}>
                            {heading}
                        </ContentTitle>
                        {subHeading && subHeading}
                    </>
                </LinkTypeResolver>
                {!editModeActivated && (expansionIconVisible || expanded) && (
                    <>
                        <ToggleExpansionButton
                            type="button"
                            onClick={toggleVisibility}
                            onBlur={() => {
                                toggleExpansionIconVisible(false);
                            }}
                        >
                            <ToggleIcon aria-hidden="true" expanded={expanded} />
                            <HiddenText>{iconText}</HiddenText>
                        </ToggleExpansionButton>
                        <HiddenText ref={a11yRef} tabIndex={-1}>
                            {a11yText}
                        </HiddenText>
                    </>
                )}
            </ContentInformation>
            <CSSTransition unmountOnExit in={showInfo} timeout={{ enter: 0, exit: 4500 }}>
                <InfoPopout url={url} index={index} description={longDescription} />
            </CSSTransition>
        </>
    );
};

export default ContentItemInfo;
